/**
 * Site-wide header component.
 * @module src/App/Header
 */

import React, {
    useState,
} from 'react';
import {
    bool, string, object,
} from 'prop-types';
import { Route, NavLink } from 'react-router-dom';
import classNames from 'classnames';

import routeConfig, { routeChildrenShape } from 'src/routeConfig';
import Dropdown from 'src/Dropdown';
import * as styles from './index.less';

/**
 * Checks if the route config object has any visible child routes.
 * @param {module:src/routeConfig~Children} children - Child routes.
 * @returns {boolean} `true` if the object has no `own` properties; `false`
 * otherwise.
 */
function hasVisibleChildRoutes(children) {
    for (const key of Object.keys(children)) {
        const child = children[key];
        if (!child.isHidden) {
            return true;
        }
    }

    return false;
}

/**
 * Logo React component.
 * @param {object} props - The object's props.
 * @param {string?} props.className - CSS class(es) for the logo.
 * @param {string?} props.to - The logo's target path.
 * @returns {React.ReactElement} The component's elements.
 */
function Logo({ className, to }) {
    className = classNames(styles.logo, className);

    const logoElems = (
        <>
            <div className={styles.image} />
            <div className={styles.text}>
                Ingleside
                <br />
                Volunteers
            </div>
        </>
    );

    if (!to) {
        return (
            <div className={className}>
                {logoElems}
            </div>
        );
    }

    return (
        <NavLink
            to={to}
            exact
            className={className}
            activeClassName={styles.selected}
        >
            {logoElems}
        </NavLink>
    );
}

Logo.propTypes = {
    className: string,
    to: string,
};

/**
 * Header link React component.
 * @param {object} props - The component's props. Will be spread onto a
 * `NavLink`.
 * @returns {React.ReactElement} The component's elements.
 */
function HeaderLink(props) {
    return (
        <NavLink
            activeClassName={styles.selected}
            {...props}
        />
    );
}

/**
 * Button React component.
 * @param {object} props - The component's props.
 * @param {boolean} props.match - Whether or not the target path has been
 * matched.
 * @param {string} props.title - The button's title.
 * @param {boolean} props.isOpen - Whether or not the dropdown is open.
 * @returns {React.ReactElement} The component's elements.
 */
function Button({
    match,
    title,
    isOpen,
}) {
    const classes = classNames(styles.button, {
        [styles.selected]: match !== null,
        [styles.open]: isOpen,
    });

    return <span className={classes}>{title}</span>;
}
Button.propTypes = {
    match: object,
    title: string,
    isOpen: bool,
};

/**
 * Dropdown button React component.
 * @param {object} props - The component's props.
 * @param {string} props.to - The dropdown's target path. Used for routing.
 * @param {string} props.title - The title for the dropdown.
 * @param {boolean} props.isOpen - Whether or not the dropdown is open.
 * @returns {React.ReactElement} The component's elements.
 */
function DropdownButton(props) {
    const { to, ...buttonProps } = props;

    return (
        <Route path={to}>
            {({ match }) => (
                <Button match={match} {...buttonProps} />
            )}
        </Route>
    );
}

DropdownButton.propTypes = {
    to: string.isRequired,
    title: string.isRequired,
    isOpen: bool,
};

/**
 * Dropdown menu React component.
 * @param {object} props - The component's props.
 * @param {string} props.to - The dropdown's target path. Used for routing.
 * @param {string} props.title - The title for the dropdown.
 * @param {object} props.ref - Ref for the menu's DOM element.
 * @param {module:src/routeConfig~Children} props.routeChildren - Child routes.
 * @returns {React.ReactElement} The component's elements.
 */
function DropdownMenu({ to, title, routeChildren, ref }) {
    return (
        <div className={styles.menu} ref={ref}>
            <HeaderLink exact to={to}>{title}</HeaderLink>
            {routeChildrenMenu(routeChildren)}
        </div>
    );
}

DropdownMenu.propTypes = {
    to: string.isRequired,
    title: string.isRequired,
    routeChildren: routeChildrenShape.isRequired,
    ref: object,
};

/**
 * Renders a menu for selecting a child route.
 * @param {module:src/routeConfig~Children} routeChildren - The child routes.
 * @returns {React.ReactElement[]} The menu's elements.
 */
function routeChildrenMenu(routeChildren) {
    const keys = Object.keys(routeChildren).sort(
        (a, b) => routeChildren[a].rank - routeChildren[b].rank,
    );
    return keys.map((name) => {
        const {
            children: grandchildren,
            title,
            isHidden,
            path,
        } = routeChildren[name];

        if (isHidden) {
            return null;
        }

        if (!hasVisibleChildRoutes(grandchildren)) {
            return (
                <HeaderLink
                    key={path}
                    to={path}
                >
                    {title}
                </HeaderLink>
            );
        }

        const button = (
            <DropdownButton
                to={path}
                title={title}
            />
        );

        const { enter, enterActive, exit, exitActive } = styles;

        return (
            <Dropdown
                key={path}
                className={styles.dropdown}
                button={button}
                transition={{
                    appear: true,
                    classNames: {
                        enter, enterActive, exit, exitActive,
                        appear: enter,
                        appearActive: enterActive,
                    },
                    timeout: 300,
                }}
            >
                <DropdownMenu
                    to={path}
                    title={title}
                    routeChildren={grandchildren}
                />
            </Dropdown>
        );
    });
}

/**
 * Header React component.
 * @returns {React.ReactElement} The component's elements.
 */
export default function Header() {
    const [isOpen, setIsOpen] = useState(false);

    /**
     * Toggle event handler.
     */
    function toggle() {
        setIsOpen(!isOpen);
    }

    const classes = classNames(styles.header, {
        [styles.open]: isOpen,
    });

    return (
        <header className={classes} onClick={toggle}>
            <Logo className={styles.toggle} />
            <nav>
                <Logo to="/" />
                <HeaderLink to="/" exact className={styles.homeLink}>
                    Home
                </HeaderLink>
                {routeChildrenMenu(routeConfig.children)}
            </nav>
        </header>
    );
}
